type Variant = 'thumb' | 'md' | 'orig';

const stripTrailing = (s: string) => s.replace(/\/+$/, '');

/**
 * Build the URL for a stored image key.
 *
 * New R2 keys (`avatars/<id>` / `banners/<id>`) are served from
 * VITE_IMAGES_BASE_URL with a `_<variant>.webp` suffix. Legacy keys
 * (`images/<file>` from the old disk storage) are served as-is from the API,
 * so previously-uploaded images keep working until they're migrated.
 *
 * Returns '' for an empty key so callers can fall back to a placeholder.
 */
export const imageSrc = (key?: string, variant: Variant = 'md'): string => {
  if (!key) return '';

  // Read env per call so the values are easy to override in tests (and Vite
  // still inlines them in the production build).
  const imagesBase = import.meta.env.VITE_IMAGES_BASE_URL as string | undefined;
  // Same default as apis/baseApi.ts so legacy keys resolve in local dev.
  const apiBase = (import.meta.env.VITE_BASE_URL as string) || 'http://localhost:3030';

  const k = key.replace(/^\/+/, '');

  const isR2Key = /^(avatars|banners)\//.test(k);
  if (isR2Key && imagesBase) {
    // Banners only have md/orig derivatives — fall back from thumb to md.
    const v = k.startsWith('banners/') && variant === 'thumb' ? 'md' : variant;
    return `${stripTrailing(imagesBase)}/${k}_${v}.webp`;
  }

  // Legacy key, or images base not configured: serve the original from the API.
  return `${stripTrailing(apiBase)}/${k}`;
};
